import axios from 'axios';
import { CURR_API } from '@/constants/apis';
import { refusalMessage } from '@/api/refusals';

/**
 * Nurse licence verification, reached only through keyon.
 *
 * This is the one place the NursyInfo and ManageNurseList envelopes are
 * spelled, so a change on keyon's side is reconciled here rather than in the
 * fourteen components that used to spell them individually.
 *
 * THE DISTINCTION THIS FILE EXISTS FOR
 * keyon answers a lookup with one of three unrelated shapes: an array of
 * licence records, or an object carrying a `status`, or nothing at all. Both
 * "this nurse is not on the list" and "we could not reach Nursys" arrive as an
 * object with a `status`, so code that branches on the presence of that key
 * treats an outage as a negative answer. On the public registration page that
 * meant a Nursys outage sent the nurse's last four SSN digits and her birth
 * year to NCSBN, and then told her the problem was her date of birth.
 * classifyLookup separates them; only NOT_ENROLLED may lead to a subscription.
 *
 * Every function takes an injectable `{ client }` so it can be tested without a
 * browser or a server.
 */

const WEBHOOK = `${CURR_API}/tswebhook.php`;

/** A lookup found the nurse; `licences` holds the records keyon returned. */
export const FOUND = 'found';
/** Nursys answered, and this nurse is not on our list. The only shape that may lead to a subscription. */
export const NOT_ENROLLED = 'not_enrolled';
/** Nursys answered and rejected the request itself - a bad licence number, a wrong jurisdiction. */
export const REJECTED = 'rejected';
/** We did not get an answer: a timeout, a transport error, a shape we do not recognise. */
export const UNAVAILABLE = 'unavailable';

/** keyon's Errors[].ErrorId for "no nurse found for the supplied licence". */
const ERROR_NOT_ENROLLED = 4;

/** What a caller says when there was no answer to read. */
export const SERVICE_UNREACHABLE =
  'We could not reach the license verification service just now. Please try again in a few minutes.';

function client(deps = {}) {
  return deps.client || axios;
}

/**
 * Was this request turned away for being too frequent?
 *
 * keyon answers NCSBN's own block with a 200 and status "Rate Limited"
 * (processClass rateLimitedAnswer), which classifyLookup already reads as
 * UNAVAILABLE. This is the same answer arriving as a rejection - a 429 from
 * keyon's per-IP counter, or that body on an error status - so it is read the
 * same way: wait and try again, nothing wrong with her details.
 */
export function isRateLimitedFailure(error) {
  const res = error && error.response;
  if (!res) return false;
  if (Number(res.status) === 429) return true;
  const data = res.data;

  return !!data && typeof data === 'object' && (data.status === 'Rate Limited' || data.code === 'rate_limited');
}

/**
 * keyon's sentence for a Nursys request axios rejected, or null when the
 * caller's "could not reach" wording is the right answer: no response at all,
 * a rate limit, or a 5xx.
 *
 * axios rejects every 401, 403, 429 and 5xx, and the lookups used to have no
 * rejection arm at all, so a lapsed session left the spinner up for good. The
 * gate in front of these branches (keyon nursysGuard) says why it refused in
 * words meant for the person, so those are shown.
 */
export function refusedRequestMessage(error) {
  const res = error && error.response;
  if (!res || Number(res.status) >= 500 || isRateLimitedFailure(error)) {
    return null;
  }

  return refusalMessage(res.data);
}

/** What to tell someone whose lookup was refused. Never empty. */
export function lookupFailureMessage(error) {
  return refusedRequestMessage(error) || SERVICE_UNREACHABLE;
}

/**
 * What a lookup response means.
 *
 * @param {*} data the raw response body from keyon
 * @returns {{kind: string, licences: Array, message: string, errorId: (number|null)}}
 */
export function classifyLookup(data) {
  if (Array.isArray(data)) {
    // keyon returns the licence rows bare on success. An empty array means
    // Nursys completed and matched nothing, which is not the same as an error
    // and is not grounds for enrolling anyone.
    return {
      kind: data.length > 0 ? FOUND : REJECTED,
      licences: data,
      message: data.length > 0 ? '' : 'Nursys returned no licence for those details.',
      errorId: null,
    };
  }

  if (!data || typeof data !== 'object' || !data.status) {
    // Null, an empty string, or an object we do not recognise. keyon's poller
    // returns null when Nursys refuses the submission outright, and a fatal
    // returns an empty body, so this is the outage case, not a negative answer.
    return { kind: UNAVAILABLE, licences: [], message: '', errorId: null };
  }

  const status = String(data.status);

  if (status === 'Result Not Found') {
    const first = Array.isArray(data.msg) && data.msg.length > 0 ? data.msg[0] : null;
    const errorId = first && first.ErrorId != null ? Number(first.ErrorId) : null;
    const message = (first && first.ErrorMessage) || '';
    if (errorId === ERROR_NOT_ENROLLED) {
      return { kind: NOT_ENROLLED, licences: [], message, errorId };
    }

    // Any other ErrorId is Nursys rejecting the request, not telling us the
    // nurse is unenrolled. Subscribing on this would file a record against
    // details Nursys has already said it cannot match.
    return { kind: REJECTED, licences: [], message, errorId };
  }

  // "No Server Response" is keyon's poll giving up, and anything else with a
  // status is a shape we have not seen. Both are outages, not answers.
  return { kind: UNAVAILABLE, licences: [], message: data.message || '', errorId: null };
}

/**
 * What an Add_Nurse / DeleteNurse response means.
 *
 * keyon returns status "Nurse Added" for a successful submission in either
 * direction - the remove path reuses the same message - so the caller cannot
 * read the word to tell which happened.
 */
export function classifyManage(data) {
  if (!data || typeof data !== 'object' || !data.status) {
    return { kind: UNAVAILABLE, message: '', errors: [] };
  }
  const status = String(data.status);
  if (status === 'Nurse Added') {
    return { kind: FOUND, message: '', errors: [] };
  }
  if (status === 'Nurse Not Added') {
    const errors = Array.isArray(data.errors) ? data.errors : [];
    return { kind: REJECTED, message: (errors[0] && errors[0].ErrorMessage) || '', errors };
  }

  return { kind: UNAVAILABLE, message: data.message || '', errors: [] };
}

/**
 * The licence record for the jurisdiction asked about, or null.
 * Nursys answers with every jurisdiction the nurse holds a licence in, which
 * is not necessarily the one the caller asked about.
 */
export function licenceForJurisdiction(licences, jurisdiction) {
  if (!Array.isArray(licences)) {
    return null;
  }

  return licences.find((l) => l && l.JurisdictionAbbreviation === jurisdiction) || null;
}

/**
 * Has this licence been restored or reinstated?
 *
 * Nursys reports discipline history as licence -> disciplines -> revision
 * reports -> revision actions, and the reinstatement is an ActionDescription
 * on the innermost one. The callers that used to do this walked the discipline
 * array with the *licence* loop's counter, so a nurse's second licence read
 * her second discipline and anything else threw on undefined.
 */
export function isLicenceReinstated(licence) {
  const disciplines = (licence && licence.NurseLookupDisciplines) || [];
  if (!Array.isArray(disciplines)) {
    return false;
  }

  return disciplines.some((discipline) => {
    const reports = (discipline && discipline.NurseLookupRevisionReports) || [];
    if (!Array.isArray(reports)) {
      return false;
    }

    return reports.some((report) => {
      const actions = (report && report.NurseLookupRevisionActions) || [];
      if (!Array.isArray(actions)) {
        return false;
      }

      return actions.some(
        (action) =>
          action && action.ActionDescription === 'LICENSE RESTORED OR REINSTATED, COMPLETE'
      );
    });
  });
}

/** Is this licence in good standing? */
export function isLicenceInGoodStanding(licence) {
  if (!licence) {
    return false;
  }
  const status = String(licence.LicenseStatus || '').toUpperCase();

  return String(licence.Active || '').toUpperCase() === 'YES' && status === 'UNENCUMBERED';
}

/**
 * Look one nurse up. Resolves to the classification, never rejects.
 *
 * A request keyon refused outright resolves as UNAVAILABLE with `refusal` set
 * to keyon's sentence when it gave one (a lapsed session, a missing
 * privilege), and null otherwise.
 */
export function lookupNurse(request, deps = {}) {
  const body = {
    NursyInfo: {
      API_Meth: 'LookupNursesInfo',
      ncsid: request.ncsid || '',
      jurisdiction: request.jurisdiction,
      rnlicense: request.rnlicense,
      ltype: request.ltype,
    },
  };
  if (request.accountnumber) {
    body.NursyInfo.accountnumber = request.accountnumber;
  }
  if (request.extern) {
    body.NursyInfo.extern = request.extern;
  }

  return client(deps)
    .post(WEBHOOK, body, deps.config || {})
    .then((res) => classifyLookup(res && res.data))
    .catch((error) => ({
      kind: UNAVAILABLE,
      licences: [],
      message: '',
      errorId: null,
      refusal: refusedRequestMessage(error),
    }));
}

/**
 * Put a nurse on the institution's eNotify list.
 *
 * `notifications` and `reminders` are required rather than defaulted: this
 * subscribes a real person to mail from the regulator's service, and eight of
 * the eleven call sites this replaces sent "Y" by copy-paste, with no screen
 * anywhere asking her.
 */
export function subscribeNurseToENotify(request, deps = {}) {
  if (request.notifications !== 'Y' && request.notifications !== 'N') {
    throw new Error('subscribeNurseToENotify: notifications must be "Y" or "N"');
  }
  if (request.reminders !== 'Y' && request.reminders !== 'N') {
    throw new Error('subscribeNurseToENotify: reminders must be "Y" or "N"');
  }

  const body = {
    ManageNurseList: {
      API_Meth: 'Add_Nurse',
      subactioncode: request.subactioncode || 'A',
      ncsid: request.ncsid || '',
      jurisdiction: request.jurisdiction,
      licensnum: request.licensnum,
      ltype: request.ltype,
      email: request.email || '',
      address1: request.address1 || '',
      address2: request.address2 || '',
      city: request.city || '',
      state: request.state || '',
      zip: request.zip || '',
      lastfourssn: request.lastfourssn || '',
      bdayear: request.bdayear || '',
      hospitalpracsetting: request.hospitalpracsetting || '',
      hostpracsettingother: request.hostpracsettingother || '',
      notficationenabled: request.notifications,
      reminderenabled: request.reminders,
      locationlist: request.locationlist || '',
      recid: request.recid || '',
    },
  };
  if (request.accountnumber) {
    body.ManageNurseList.accountnumber = request.accountnumber;
  }

  return client(deps)
    .post(WEBHOOK, body, deps.config || {})
    .then((res) => classifyManage(res && res.data))
    .catch(() => ({ kind: UNAVAILABLE, message: '', errors: [] }));
}
